import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import { Knob } from "primereact/knob";
import { Checkbox } from "primereact/checkbox";
import { ProgressBar } from "primereact/progressbar";

import MonthDropdown from "../../../../components/inputs/MonthDropdown";
import { previousMonthPeriod } from "../../../../utils/previousMonth";
import YearDropdown from "../../../../components/inputs/YearDropdown";
import NumberInputField from "../../../../components/inputs/NumberInputField";
import { InputLabel } from "../../../../components/inputs/InputLabel";
import { ResultsDisplayHeader } from "./components/ResultsDisplayHeader";
import { FinalizeDialog } from "./components/FinalizeDialog";
import type { ValidationInfo } from "./components/FinalizeDialog";
import { EroundPrefillHint } from "./components/EroundPrefillHint";

import {
  clearingValidationEmpBills,
  clearingValidateVat,
} from "../../../../services/clearing/validation";
import { isProduction } from "../../../../utils/environment";
import { useEroundPrefill } from "./hooks/useEroundPrefill";

type EmpBillsValidationResult = {
  passed: boolean;
  sumOfEroundCdrs: number;
  empBillsSum: number;
  errorRate: number;
  difference: number;
  month: number;
  year: number;
  message: string;
  numberOfDocuments: number;
  empBills: ValidateableEmpBill[];
};

type ValidateableEmpBill = {
  documentId: number | null;
  title: string | null;
  amountNet: number | null;
  amount: number | null;
  vatOption: string | null;
  dueInDays: number | null;
  customerId: number | null;
  vatId: string | null;
  serviceDate: object | null;
  invoiceEmail: string | null;
  easybillContactId: number | null;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
};

const FINALIZE_DELAY_SECONDS = isProduction ? 3 : 10;

// Validation runs once a month has closed, so default to the previous month.
const initialSelectedPeriod = previousMonthPeriod();

const isAfterMarch2026 = (period: { month: number; year: number }) =>
  period.year > 2026 || (period.year === 2026 && period.month >= 3);

const isAfterJanuary2026 = (period: { month: number; year: number }) => period.year >= 2026;

export function EmpBillsValidationForm({ visible, onClose }) {
  const toast = useRef(null);

  const [selectedPeriod, setSelectedPeriod] = useState(initialSelectedPeriod);
  const { eroundSum, setEroundSum, eroundPrefilled } = useEroundPrefill(selectedPeriod);

  const [enforceTitlePrefix, setEnforceTitlePrefix] = useState(isAfterMarch2026(initialSelectedPeriod));
  const [skipWithoutServiceDate, setSkipWithoutServiceDate] = useState(isAfterJanuary2026(initialSelectedPeriod));

  // The form stays mounted while the dialog is hidden, so a tab left open into a new
  // month would keep the old default. Re-default only when the default itself moved,
  // so a period picked by hand (and its results) survive closing and reopening.
  const defaultPeriodRef = useRef(initialSelectedPeriod);
  useEffect(() => {
    if (!visible) return;
    const defaultPeriod = previousMonthPeriod();
    const lastDefault = defaultPeriodRef.current;
    if (defaultPeriod.month === lastDefault.month && defaultPeriod.year === lastDefault.year) return;
    defaultPeriodRef.current = defaultPeriod;
    setSelectedPeriod(defaultPeriod);
    setEnforceTitlePrefix(isAfterMarch2026(defaultPeriod));
    setSkipWithoutServiceDate(isAfterJanuary2026(defaultPeriod));
  }, [visible]);

  const [validationResults, setValidationResults] =
    useState<EmpBillsValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [vatLoading, setVatLoading] = useState(false);

  const [vatWarnings, setVatWarnings] = useState<ValidationInfo[] | null>(null);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);

  const getValidationResults = useCallback(async () => {
    setLoading(true);
    try {
      const results = await clearingValidationEmpBills({
        month: selectedPeriod.month,
        year: selectedPeriod.year,
        eroundSum,
        enforceTitlePrefix,
        skipWithoutServiceDate,
      });
      setValidationResults(results);
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: (err as Error)?.message || "Failed to complete EMP bills validation.",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, eroundSum, enforceTitlePrefix, skipWithoutServiceDate, setValidationResults, toast]);

  const handleValidateVat = useCallback(async () => {
    if (!validationResults?.empBills?.length) return;

    setVatLoading(true);
    const warnings = await clearingValidateVat({
      empBills: validationResults.empBills,
      month: validationResults.month,
      year: validationResults.year,
      enforceTitlePrefix,
      skipWithoutServiceDate,
    });

    if (warnings) {
      setVatWarnings(warnings);
      setShowFinalizeDialog(true);
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to validate VAT.",
      });
    }

    setVatLoading(false);
  }, [validationResults, enforceTitlePrefix, skipWithoutServiceDate, toast]);

  const handleCloseFinalizeDialog = () => {
    setShowFinalizeDialog(false);
    setVatWarnings(null);
  };

  return (
    <>
      <Dialog
        header="EMP Bills Validation"
        visible={visible && !showFinalizeDialog}
        style={{ width: "55vw" }}
        onHide={onClose}
      >
        <Toast ref={toast} />

        <section className="flex flex-column">
          <div className="flex flex-column">
            <div>
              <InputLabel label={"Period"} required />
              <div className="flex flex-column sm:flex-row gap-2 field">
                <MonthDropdown
                  monthIndex={selectedPeriod.month}
                  onChange={(option) => {
                    const month = Number(option.code);
                    setSelectedPeriod((prev) => ({ ...prev, month }));
                    setEnforceTitlePrefix(
                      selectedPeriod.year > 2026 ||
                      (selectedPeriod.year === 2026 && month >= 3)
                    );
                    setSkipWithoutServiceDate(selectedPeriod.year >= 2026);
                  }}
                />
                <YearDropdown
                  year={selectedPeriod.year}
                  onChange={(option) => {
                    const year = Number(option.code);
                    setSelectedPeriod((prev) => ({ ...prev, year }));
                    setEnforceTitlePrefix(
                      year > 2026 ||
                      (year === 2026 && selectedPeriod.month >= 3)
                    );
                    setSkipWithoutServiceDate(year >= 2026);
                  }}
                />
              </div>
            </div>

            <NumberInputField
              inputClassName="w-1rem sm:w-25rem sm:h-3rem flex"
              name="eroundSum"
              label="Eround CDRs Total (optional)"
              value={eroundSum}
              onChange={(e) => setEroundSum(e.value)}
              min={0}
              step={0.01}
              minFractionDigits={2}
              maxFractionDigits={2}
              mode="currency"
              locale="de-DE"
              showButtons
              containerClassName="p-0 field"
            />
            <small className="text-color-secondary -mt-2 mb-3 block">
              Leave blank to fetch automatically from eRound; enter a value only to override.
            </small>
            <EroundPrefillHint visible={eroundPrefilled} />

            <div className="flex flex-column gap-2 field">
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="enforceTitlePrefix"
                  checked={enforceTitlePrefix}
                  onChange={(e) => setEnforceTitlePrefix(e.checked)}
                />
                <label htmlFor="enforceTitlePrefix">
                  Only validate bills with the prefix EMP-YYDD- (recommended from March 2026)
                </label>
              </div>
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="skipWithoutServiceDate"
                  checked={skipWithoutServiceDate}
                  onChange={(e) => setSkipWithoutServiceDate(e.checked)}
                />
                <label htmlFor="skipWithoutServiceDate">
                  Do not validate bills without ServiceDate (recommended from January 2026)
                </label>
              </div>
            </div>
          </div>

          <Button
            className="w-16rem sm:h-3rem sm:mt-2"
            style={{ alignSelf: "end" }}
            label="Validate EMP Bills"
            icon="pi pi-check-circle"
            onClick={getValidationResults}
            loading={loading}
          />
        </section>

        {loading && (
          <div className="mt-3">
            <p className="text-center text-sm text-color-secondary mb-2">Validating EMP bills...</p>
            <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
          </div>
        )}

        {validationResults && (
          <EmpBillsResults
            results={validationResults}
            onValidateVat={handleValidateVat}
            vatLoading={vatLoading}
          />
        )}
      </Dialog>

      {showFinalizeDialog && vatWarnings && (
        <FinalizeDialog
          visible={showFinalizeDialog}
          warnings={vatWarnings}
          onClose={handleCloseFinalizeDialog}
          header="Finalize and Send Bills"
          buttonLabel="Finalize and Send Validated Documents"
          successMessage="Finalized all documents successfully."
          delaySeconds={FINALIZE_DELAY_SECONDS}
        />
      )}
    </>
  );
}

function EmpBillsResults({
  results,
  onValidateVat,
  vatLoading,
}: {
  results: EmpBillsValidationResult;
  onValidateVat: () => void;
  vatLoading: boolean;
}) {
  const knobColor = results.passed ? "#22C55E" : "#EF4444";
  const vatEnabled = results.errorRate < 1;

  return (
    <section>
      <Divider />

      <ResultsDisplayHeader monthIndex={results.month} year={results.year} />

      <section className="my-4 flex flex-row justify-content-around align-items-center">
        <div className="flex flex-column align-items-center">
          <Knob
            value={Math.round(results.errorRate * 100) / 100}
            valueColor={knobColor}
            readOnly
            size={120}
            max={100}
            valueTemplate={`${(Math.round(results.errorRate * 100) / 100).toFixed(2)}%`}
          />
          <span
            className="mt-2 font-bold"
            style={{ color: knobColor }}
          >
            {results.passed ? "PASSED" : "FAILED"}
          </span>
        </div>

        <div className="flex flex-column gap-2">
          <div className="flex justify-content-between gap-4">
            <span className="font-semibold">Eround CDRs Sum:</span>
            <span>
              {results.sumOfEroundCdrs?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex justify-content-between gap-4">
            <span className="font-semibold">EMP Bills Sum:</span>
            <span>
              {results.empBillsSum?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <Divider className="my-1" />
          <div className="flex justify-content-between gap-4">
            <span className="font-semibold">Difference:</span>
            <span>
              {results.difference?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex justify-content-between gap-4">
            <span className="font-semibold">Number of Documents:</span>
            <span>{results.numberOfDocuments}</span>
          </div>
          <div className="flex justify-content-between gap-4">
            <span className="font-semibold">Message:</span>
            <span>{results.message}</span>
          </div>
        </div>
      </section>

      <div className="flex justify-content-end mt-3">
        <Button
          label="Validate VAT"
          icon="pi pi-verified"
          onClick={onValidateVat}
          loading={vatLoading}
          disabled={!vatEnabled}
          tooltip={!vatEnabled ? "Error rate must be under 1% to validate VAT" : undefined}
          tooltipOptions={{ position: "left" }}
        />
      </div>

      {vatLoading && (
        <div className="mt-3">
          <p className="text-center text-sm text-color-secondary mb-2">Validating VAT options...</p>
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        </div>
      )}
    </section>
  );
}

